import { Box, Icon, List, ListItem, Text, Tooltip, TooltipContent } from '@customerio/pluma-components/react';
import { ObjectAttributeValue } from '@/features/objects';
import { AttributeTypeLabel, CREATED_IN_CIO_AT, IdentifierKey, type IdentifiersConfig } from '@/features/people';

type RelationshipAttributeListProps = {
  /** Ordered `{ key: value }` pairs. */
  attributes: ReadonlyArray<Record<string, string>>;
  /** The workspace's person identifiers: a relationship attribute sharing one's name is still labelled Reserved. */
  identifiers: IdentifiersConfig | null | undefined;
};

type RelationshipAttributeItemProps = {
  attributeKey: string;
  value: string;
  identifiers: IdentifiersConfig | null | undefined;
};

/** The help marker Ember shows on a name Customer.io generates itself. */
function AutoGeneratedMarker() {
  return (
    <Tooltip placement="top" isCustom>
      {(context) => (
        <>
          <Icon
            name="help"
            size="sm"
            color="subtle"
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
          />
          <TooltipContent>Auto-generated by Customer.io.</TooltipContent>
        </>
      )}
    </Tooltip>
  );
}

function RelationshipAttributeItem({ attributeKey, value, identifiers }: RelationshipAttributeItemProps) {
  const isAutoGenerated = attributeKey === IdentifierKey.CioId || attributeKey === CREATED_IN_CIO_AT;

  return (
    <ListItem display="flex" alignItems="center" px="0" py="100">
      <Box flex={1}>
        <Text as="span" display="flex" alignItems="center" weight="regular" color="subtle" overflowWrap="words">
          <Text as="span" pr="025" data-test-cio-person-attribute-key>
            {attributeKey}
          </Text>
          {isAutoGenerated ? <AutoGeneratedMarker /> : null}
          <AttributeTypeLabel attribute={attributeKey} identifiers={identifiers} />
        </Text>
        <Box data-test-cio-person-attribute-value>
          <ObjectAttributeValue value={value} />
        </Box>
      </Box>
    </ListItem>
  );
}

/**
 * A relationship's attributes, read-only. Ember renders them through the
 * person attribute list, so a key reads with the person rules: reserved
 * person names are badged, identifiers are not (the list ignores them).
 */
export function RelationshipAttributeList({ attributes, identifiers }: RelationshipAttributeListProps) {
  return (
    <List styleType="none" gap="100">
      {attributes.map((attribute) => {
        const [key = ''] = Object.keys(attribute);
        return (
          <RelationshipAttributeItem
            key={key}
            attributeKey={key}
            value={attribute[key] ?? ''}
            identifiers={identifiers}
          />
        );
      })}
    </List>
  );
}
